import { z } from 'zod';
export const AuditActionSchema = z.enum([
    'create',
    'update',
    'delete',
    'login',
    'logout',
    'api_key_created',
    'api_key_revoked',
    'export_created',
    'payment_processed',
    'webhook_created',
    'webhook_triggered',
    'contract_created',
    'contract_cancelled',
    'invoice_generated',
    'user_invited',
    'permission_granted',
    'permission_revoked',
]);
export const AUDIT_ACTION = AuditActionSchema.enum;
export const AuditResourceTypeSchema = z.enum([
    'agent',
    'customer',
    'contract',
    'invoice',
    'api_key',
    'webhook',
    'user',
    'organization',
    'payment',
    'export',
    'signal',
    'feature',
    'plan_template',
]);
export const AUDIT_RESOURCE_TYPE = AuditResourceTypeSchema.enum;
export const AuditLogsQuerySchema = z.object({
    page: z.coerce.number().min(1).default(1).optional(),
    limit: z.coerce.number().min(1).max(100).default(20).optional(),
    action: AuditActionSchema.optional(),
    resource_type: AuditResourceTypeSchema.optional(),
    user_id: z.string().optional(),
    resource_id: z.string().optional(),
    start_date: z.string().optional(),
    end_date: z.string().optional(),
    ip_address: z.string().optional(),
    user_agent: z.string().optional(),
});
export const AuditLogSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    user_id: z.string().nullable(),
    user_email: z.string().nullable(),
    api_key_id: z.string().nullable(),
    action: z.string(),
    resource_type: z.string(),
    resource_id: z.string().nullable(),
    details: z.record(z.string(), z.unknown()),
    ip_address: z.string().nullable(),
    user_agent: z.string().nullable(),
    timestamp: z.string(),
});
export const AuditLogsResponseSchema = z.object({
    logs: z.array(AuditLogSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        total_pages: z.number(),
    }),
});
export const SimpleAuditLogsResponseSchema = z.object({
    data: z.array(z.object({
        id: z.string(),
        user_id: z.string().optional(),
        user_email: z.string().optional(),
        action: z.string(),
        resource_type: z.string(),
        resource_id: z.string().optional(),
        timestamp: z.string(),
        ip_address: z.string().optional(),
        user_agent: z.string().optional(),
        details: z.record(z.string(), z.unknown()).optional(),
    })),
});
export const SystemHealthSchema = z.object({
    status: z.enum(['healthy', 'degraded', 'unhealthy']),
    timestamp: z.string(),
    services: z.object({
        database: z.object({
            status: z.enum(['up', 'down']),
            response_time_ms: z.number().nullable(),
            last_check: z.string(),
        }),
        redis: z.object({
            status: z.enum(['up', 'down']),
            response_time_ms: z.number().nullable(),
            last_check: z.string(),
        }),
        clickhouse: z.object({
            status: z.enum(['up', 'down']),
            response_time_ms: z.number().nullable(),
            last_check: z.string(),
        }),
        s3: z.object({
            status: z.enum(['up', 'down']),
            response_time_ms: z.number().nullable(),
            last_check: z.string(),
        }),
        kafka: z.object({
            status: z.enum(['up', 'down']),
            response_time_ms: z.number().nullable(),
            last_check: z.string(),
        }),
    }),
    uptime_seconds: z.number(),
    version: z.string(),
    environment: z.string(),
});
export const SystemMetricsSchema = z.object({
    timestamp: z.string(),
    api: z.object({
        requests_per_minute: z.number(),
        average_response_time_ms: z.number(),
        error_rate: z.number(),
        active_connections: z.number(),
    }),
    database: z.object({
        connection_pool_size: z.number(),
        active_connections: z.number(),
        query_count_per_minute: z.number(),
        average_query_time_ms: z.number(),
    }),
    memory: z.object({
        used_mb: z.number(),
        total_mb: z.number(),
        usage_percentage: z.number(),
    }),
    cpu: z.object({
        usage_percentage: z.number(),
        load_average: z.array(z.number()),
    }),
    events: z.object({
        processed_per_minute: z.number(),
        failed_per_minute: z.number(),
        queue_depth: z.number(),
    }),
    billing: z.object({
        invoices_generated_today: z.number(),
        payments_processed_today: z.number(),
        total_revenue_today: z.number(),
    }),
});
export const SystemLimitsSchema = z.object({
    tenant_id: z.string(),
    current_usage: z.object({
        api_requests_today: z.number(),
        events_processed_today: z.number(),
        storage_used_mb: z.number(),
        active_webhooks: z.number(),
        active_exports: z.number(),
        monthly_invoice_count: z.number(),
    }),
    limits: z.object({
        max_api_requests_per_day: z.number(),
        max_events_per_day: z.number(),
        max_storage_mb: z.number(),
        max_webhooks: z.number(),
        max_concurrent_exports: z.number(),
        max_invoices_per_month: z.number(),
    }),
    usage_percentages: z.object({
        api_requests: z.number(),
        events: z.number(),
        storage: z.number(),
        webhooks: z.number(),
        exports: z.number(),
        invoices: z.number(),
    }),
    warnings: z.array(z.object({
        metric: z.string(),
        current_value: z.number(),
        limit_value: z.number(),
        percentage: z.number(),
        threshold_exceeded: z.boolean(),
    })),
});
export const CreateAuditLogSchema = z.object({
    user_id: z.string().optional(),
    user_email: z.string().optional(),
    api_key_id: z.string().optional(),
    action: z.string(),
    resource_type: z.string(),
    resource_id: z.string().optional(),
    details: z.record(z.string(), z.unknown()).optional(),
    ip_address: z.string().optional(),
    user_agent: z.string().optional(),
});
//# sourceMappingURL=index.js.map